import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { DashboardHeader, StatCard, Tabs, StatusBadge, Empty, fmtTs } from "../components/DashboardUI";
import ChangePasswordModal from "../components/ChangePasswordModal";

export default function ReceptionDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [b, c, s, a] = await Promise.all([
        api.get("/bookings"),
        api.get("/staff/customers"),
        api.get("/staff/stats"),
        api.get("/owner/staff").catch(() => ({ data: [] })), // reception may not have access; fallback empty
      ]);
      setBookings(b.data); setCustomers(c.data); setStats(s.data);
      setAgents((a.data || []).filter(x => x.role === "agent"));
    } catch { nav("/reception/login"); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/reception/login");
    if (user.role !== "receptionist" && user.role !== "owner" && user.role !== "admin") return nav("/");
    load();
  }, [user, loading]);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      // Auto-open WhatsApp confirmation/decline message in a new tab
      if (r.data?.whatsapp_link) window.open(r.data.whatsapp_link, "_blank", "noopener");
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Permanently delete this booking? This cannot be undone.")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Booking deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const assignStaff = async (staffId) => {
    if (!assignTarget) return;
    try {
      await api.post(`/bookings/${assignTarget.id}/assign-staff`, { staff_id: staffId });
      toast.success("Staff assigned");
      setAssignTarget(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const submitNote = async () => {
    if (!noteTarget) return;
    try {
      await api.post(`/bookings/${noteTarget.id}/cancel`, { receptionist_note: noteTarget.note });
      toast.success("Note added to cancellation");
      setNoteTarget(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="reception-dashboard">
      <DashboardHeader title="Reception" user={user} onLogout={logout} onChangePassword={() => setPwdOpen(true)} />
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Customers" value={stats.customers} />
          <StatCard label="Pending Bookings" value={stats.bookings_pending} accent />
          <StatCard label="Total Bookings" value={stats.bookings_today_or_later} />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <Tabs current={tab} onChange={setTab} prefix="reception-tab" tabs={[
          { k: "bookings", label: "Bookings", count: stats?.bookings_pending },
          { k: "customers", label: "Customers" },
        ]} />
        <div className="mt-6 pb-16">
          {tab === "bookings" && (
            <BookingsManaged
              items={bookings} agents={agents}
              onUpdate={updateStatus}
              onAssign={(b) => setAssignTarget(b)}
              onNote={(b) => setNoteTarget({ id: b.id, note: "" })}
              onDelete={deleteBooking}
            />
          )}
          {tab === "customers" && <CustomersList items={customers} />}
        </div>
      </div>

      {assignTarget && (
        <Modal title={`Assign staff for ${assignTarget.name}`} onClose={() => setAssignTarget(null)} testid="assign-modal">
          <p className="text-sm text-[var(--muted)] mb-4">Pick which agent will handle {assignTarget.service}.</p>
          {agents.length === 0 && <p className="text-sm text-[var(--muted)]">No agents available. Owner can create staff at <code>/admin-owner</code>.</p>}
          <div className="space-y-2">
            {agents.map(a => (
              <button key={a.id} onClick={() => assignStaff(a.id)} className="w-full text-left p-3 border border-[var(--border)] hover:border-[var(--accent)] rounded" data-testid={`assign-${a.id}`}>
                <p className="font-medium">{a.name}</p>
                <p className="text-xs text-[var(--muted)]">{a.email}</p>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {noteTarget && (
        <Modal title="Cancellation Note" onClose={() => setNoteTarget(null)} testid="note-modal">
          <p className="text-sm text-[var(--muted)] mb-3">Add a note for the owner about why this customer cancelled.</p>
          <textarea value={noteTarget.note} onChange={e => setNoteTarget(t => ({...t, note: e.target.value}))}
            rows={4} className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 mb-4" data-testid="note-text" />
          <button onClick={submitNote} className="btn-gold w-full rounded-full py-2.5 text-xs uppercase tracking-widest" data-testid="note-submit">Save Note</button>
        </Modal>
      )}

      <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}

function Modal({ title, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white max-w-md w-full border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] bg-[var(--bg)] flex justify-between items-center">
          <h3 className="font-display text-2xl">{title}</h3>
          <button onClick={onClose} className="text-2xl">×</button>
        </div>
        <div className="p-7">{children}</div>
      </div>
    </div>
  );
}

function BookingsManaged({ items, agents, onUpdate, onAssign, onNote }) {
  if (!items.length) return <Empty msg="No bookings." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Guest</th><th className="text-left p-4">Service</th><th className="text-left p-4">Date / Time</th><th className="text-left p-4">WhatsApp</th><th className="text-left p-4">Assigned</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th></tr>
        </thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} className="border-t border-[var(--border)]" data-testid={`reception-booking-${b.id}`}>
              <td className="p-4">{b.name}{b.notes && <p className="text-xs text-[var(--muted)] italic mt-1">"{b.notes}"</p>}</td>
              <td className="p-4">{b.service}</td>
              <td className="p-4">{b.date}<br/><span className="text-xs text-[var(--muted)]">{b.time}</span></td>
              <td className="p-4 text-xs">
                <a href={`https://wa.me/${(b.phone||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline">
                  <i className="fa-brands fa-whatsapp"></i> {b.phone}
                </a>
              </td>
              <td className="p-4 text-xs">{b.assigned_staff_name || <span className="text-[var(--muted)]">—</span>}</td>
              <td className="p-4">
                <StatusBadge s={b.status} />
                {b.cancellation && <p className="text-[10px] text-[var(--muted)] italic mt-1">"{b.cancellation.reason || '—'}"</p>}
              </td>
              <td className="p-4">
                <div className="flex gap-2 justify-end flex-wrap">
                  {b.status === "pending" && <button onClick={() => onUpdate(b.id, "approved")} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded" data-testid={`reception-approve-${b.id}`}>Confirm</button>}
                  {b.status !== "cancelled" && agents.length > 0 && (
                    <button onClick={() => onAssign(b)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`reception-assign-${b.id}`}>Assign</button>
                  )}
                  {b.status === "cancelled" && (
                    <button onClick={() => onNote(b)} className="text-xs px-3 py-1.5 border border-[var(--border)] rounded hover:bg-[var(--bg)]" data-testid={`reception-note-${b.id}`}>Note</button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomersList({ items }) {
  if (!items.length) return <Empty msg="No customers." />;
  return (
    <div className="bg-white border border-[var(--border)] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg)] text-xs uppercase tracking-widest text-[var(--muted)]">
          <tr><th className="text-left p-4">Name</th><th className="text-left p-4">Phone</th><th className="text-left p-4">Email</th><th className="text-left p-4">Visits</th><th className="text-left p-4">Last Service</th><th className="text-right p-4">Report</th></tr>
        </thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`reception-customer-${c.id}`}>
              <td className="p-4 font-medium">{c.name}</td>
              <td className="p-4">{c.phone}</td>
              <td className="p-4 text-xs">{c.email}</td>
              <td className="p-4">{c.bookings_count}</td>
              <td className="p-4 text-xs">{c.last_service ? `${c.last_service} · ${c.last_visit}` : "—"}</td>
              <td className="p-4 text-right">
                <button onClick={() => downloadCustomerReceipt(c.id, c.name)} className="text-xs px-3 py-1.5 border border-[var(--border)] hover:bg-[var(--bg)] rounded" data-testid={`pdf-${c.id}`}>
                  <i className="fa-regular fa-file-pdf mr-1"></i> PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
